"use client";

import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertCircle, Banknote, CheckCircle2, CircleDollarSign, ReceiptText, WalletCards } from "lucide-react";
import MetricCard from "./MetricCard";
import { money, pct } from "./utils";

export default function OverviewTab({ state }) {
  const { income, expense, multiplier, dashboard } = state;
  const serverIncome = dashboard?.income;
  const serverExpense = dashboard?.expense;
  const collected = serverIncome?.collected ?? income.collected * multiplier;
  const billed = serverIncome?.billed ?? income.billed * multiplier;
  const spending = serverExpense?.monthly ?? expense.monthly * multiplier;
  const pending = Math.max(billed - collected, 0);
  const balance = dashboard?.balance ?? collected - spending;
  const collectionRate = dashboard?.collection_rate ?? (billed ? (collected / billed) * 100 : 0);
  const cashFlow = [
    { name: "Collected", value: collected, fill: "#2563eb" },
    { name: "Spent", value: spending, fill: "#f59e0b" },
    { name: "Balance", value: balance, fill: balance >= 0 ? "#16a34a" : "#dc2626" },
  ];
  const categorySource = serverExpense?.by_category || expense.byCategory;
  const categories = Object.entries(categorySource)
    .sort(([, left], [, right]) => right - left)
    .slice(0, 5)
    .map(([name, value]) => ({ name, value: serverExpense ? value : value * multiplier }));

  return (
    <div className="space-y-6">
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500">Current cash position</p>
          <h2 className="mt-1 text-2xl font-bold text-slate-800">
            {balance >= 0 ? "Surplus" : "Deficit"}: <span className={balance >= 0 ? "text-emerald-600" : "text-rose-600"}>{money(Math.abs(balance))}</span>
          </h2>
          <p className="mt-2 text-sm text-slate-500">Based on collected income minus expenditure for the selected period.</p>
        </div>
        <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <WalletCards className="w-5 h-5 text-slate-500" />
          <span>Collection rate <strong className="ml-1 text-slate-900">{pct(collectionRate)}</strong></span>
        </div>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard variant="blue" label="Collected income" value={money(collected)} note="Amounts received in the selected period." icon={<Banknote className="w-6 h-6 text-blue-600" />} />
        <MetricCard variant="blue" label="Income billed" value={money(billed)} note="Expected income after concessions." icon={<ReceiptText className="w-6 h-6 text-indigo-600" />} />
        <MetricCard variant="orange" label="Expenditure" value={money(spending)} note="Operating and capital costs." icon={<WalletCards className="w-6 h-6 text-orange-600" />} />
        <MetricCard variant={balance >= 0 ? "green" : "red"} label="Net cash position" value={money(balance)} note="Collected income less expenditure." icon={<CircleDollarSign className={`w-6 h-6 ${balance >= 0 ? "text-emerald-600" : "text-rose-600"}`} />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartPanel title="Cash flow" subtitle="Income collected, spending, and current balance.">
          <BarChart data={cashFlow} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={money} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={72} />
            <Tooltip formatter={money} />
            <Bar dataKey="value" radius={[5, 5, 0, 0]}>{cashFlow.map((item) => <Cell key={item.name} fill={item.fill} />)}</Bar>
          </BarChart>
        </ChartPanel>
        <ChartPanel title="Top expenditure categories" subtitle="The five highest cost categories in the selected period.">
          {categories.length ? (
            <BarChart data={categories} layout="vertical" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tickFormatter={money} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={money} />
              <Bar dataKey="value" fill="#64748b" radius={[0, 5, 5, 0]} />
            </BarChart>
          ) : (
            <div className="flex h-full items-center justify-center text-sm font-medium text-slate-400 ">
            </div>
          )}
        </ChartPanel>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <h2 className="text-lg font-semibold text-slate-800">Financial attention points</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Insight icon={collectionRate >= 90 ? <CheckCircle2 /> : <AlertCircle />} title="Collections" text={collectionRate >= 90 ? "Collection is on track." : `${money(pending)} remains uncollected from billed income.`} tone={collectionRate >= 90 ? "good" : "warning"} />
          <Insight icon={balance >= 0 ? <CheckCircle2 /> : <AlertCircle />} title="Cash position" text={balance >= 0 ? "Collected income currently covers expenditure." : "Expenditure is higher than collected income."} tone={balance >= 0 ? "good" : "warning"} />
          <Insight icon={<WalletCards />} title="Next review" text="Review the highest expenditure category and overdue collection list before the next month closes." tone="neutral" />
        </div>
      </section>
    </div>
  );
}

function ChartPanel({ title, subtitle, children }) {
  return <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5"><h2 className="text-lg font-semibold text-slate-800">{title}</h2><p className="mt-1 text-sm text-slate-500">{subtitle}</p><div className="mt-4 h-64"><ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer></div></section>;
}

function Insight({ icon, title, text, tone }) {
  const tones = { good: "bg-emerald-50 text-emerald-700", warning: "bg-amber-50 text-amber-700", neutral: "bg-slate-100 text-slate-600" };
  return <div className="rounded-lg border border-slate-200 p-4"><div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>{icon}</div><h3 className="mt-3 text-sm font-semibold text-slate-800">{title}</h3><p className="mt-1 text-sm leading-5 text-slate-500">{text}</p></div>;
}
